import { EnvironmentProviders, Injectable, inject, makeEnvironmentProviders } from '@angular/core';
import { Observable, throwError, tap, map } from 'rxjs';
import { FileDownloader } from '@lib.core/utils/file-downloader';
import {
  CsvDownloadStrategy,
  DocxDownloadStrategy,
  DOWNLOAD_STRATEGY,
  ElementPdfDownloadStrategy,
  PdfDownloadStrategy,
  PngDownloadStrategy,
  SvgDownloadStrategy,
} from '@lib.publications/content-actions/download-strategy';
import { ExportSource } from '@lib.publications/content-actions/export-source';
import { DownloadFormat } from '@lib.publications/content-actions/types';

@Injectable({ providedIn: 'root' })
export class DownloadInteractor {
  private readonly strategies = inject(DOWNLOAD_STRATEGY);

  availableFileTypes(source: ExportSource): readonly DownloadFormat[] {
    return [...new Set(this.strategies.filter(strategy => strategy.supports(source)).map(strategy => strategy.fileType))];
  }

  download(fileType: DownloadFormat, source: ExportSource): Observable<void> {
    const strategy = this.strategies.find(item => item.fileType === fileType && item.supports(source));
    if (!strategy) {
      return throwError(() => new Error(`No download strategy for ${fileType}`));
    }
    const fileName = `${source.exportFileName}.${fileType}`;
    return strategy.createBlob(source).pipe(
      tap(blob => FileDownloader.triggerDownload(blob, fileName)),
      map(() => undefined),
    );
  }
}

export function provideDownloads(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: DOWNLOAD_STRATEGY, useClass: PngDownloadStrategy, multi: true },
    { provide: DOWNLOAD_STRATEGY, useClass: SvgDownloadStrategy, multi: true },
    { provide: DOWNLOAD_STRATEGY, useClass: PdfDownloadStrategy, multi: true },
    { provide: DOWNLOAD_STRATEGY, useClass: ElementPdfDownloadStrategy, multi: true },
    { provide: DOWNLOAD_STRATEGY, useClass: CsvDownloadStrategy, multi: true },
    { provide: DOWNLOAD_STRATEGY, useClass: DocxDownloadStrategy, multi: true },
  ]);
}
